<template>
  <div>
    <template v-if="!isFailedPaymentsEmpty">
      <div class="box main-box is-radiusless is-shadowless" style="position: relative">
        <h2>Retry Failed Payments</h2>
        <b-field class="section-title">
          <div class="columns">
            <div class="column mw-320">
              <div class="block">
                <p class="has-text-weight-semibold"
                   v-for="item in failedPayment.descriptions" v-bind:key="item">{{item}}</p>
                <p v-if="subscriptionPeriod"
                   class="has-text-weight-semibold">{{subscriptionPeriod}}</p>
              </div>
              <div class="block">
                <p>Invoice number {{failedPayment.number}}</p>
                <p v-if="failedPayment.failureMessage">Note: {{failedPayment.failureMessage}}</p>
              </div>
            </div>
            <div class="column is-narrow">
              <div class="buttons is-pulled-right">
                <b-button class="button is-success" @click="onPay">
                  Pay {{ failedPayment.amount | currency(2) }}
                </b-button>
              </div>
            </div>
          </div>
        </b-field>
        <nav v-if="total > 1" class="pagination is-small is-simple"
             role="navigation" aria-label="pagination">
          <small class="info">{{page}} of {{total}}</small>
          <b-button @click="onPrevPage" :disabled="page===1"
                    class="pagination-previous pagination-link">
            <span class="icon" aria-hidden="true">
              <i class="mdi mdi-chevron-left mdi-24px"></i>
            </span>
          </b-button>
          <b-button class="pagination-next pagination-link"
                    :disabled="page >= total"
                    @click="onNextPage">
            <span class="icon" aria-hidden="true">
              <i class="mdi mdi-chevron-right mdi-24px"></i>
            </span>
          </b-button>
        </nav>
        <b-loading :is-full-page="false" :active="preloading" :can-cancel="false"></b-loading>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import {
  paymentsFailed, makePayment, updateStatusInvoice,
} from '@/api/card_payments';
import { VueMaskDirective } from 'v-mask';

export default {
  name: 'ContractorFailedPayments',
  directives: { mask: VueMaskDirective },
  data() {
    return {
      failedPayment: [],
      total: 0,
      page: 1,
      perPage: 1,
      number: '',
      preloading: false,
    };
  },
  computed: {
    ...mapState('user', { userId: (state) => state.id }),
    ...mapGetters(['isSwitched', 'userAuthenticator', 'authAuthenticator']),
    isFailedPaymentsEmpty() {
      return this.failedPayment.length === 0;
    },
    subscriptionPeriod() {
      if (this.failedPayment.currentPeriodStart && this.failedPayment.currentPeriodEnd) {
        return `${this.dateToString(new Date(this.failedPayment.currentPeriodStart))} -
        ${this.dateToString(new Date(this.failedPayment.currentPeriodEnd))}`;
      }
      return '';
    },
  },
  activated() {
    const params = (new URL(document.location)).searchParams;
    this.preloading = true;
    if (params.get('payment_intent')) {
      const newUrl = document.location.href.substring(0, document.location.href.indexOf('?'));
      window.history.pushState({}, '', newUrl);
      updateStatusInvoice(params.get('payment_intent'), { userId: this.userId }).then((response) => {
        if (response.data.error) {
          this.toast('Payment is failed', 'is-danger');
        } else if (response.data.status === 'paid') {
          this.toast('Your payment has been completed successfully');
        } else {
          this.toast('Payment is failed', 'is-danger');
        }
      }).finally(() => {
        this.loadFailedPayments().finally(() => {
          this.preloading = false;
        });
      });
    } else {
      this.loadFailedPayments().finally(() => {
        this.preloading = false;
      });
    }
  },

  methods: {
    onPay() {
      this.preloading = true;
      makePayment(this.failedPayment.id, { userId: this.userId }).then((response) => {
        this.page = 1;
        this.loadFailedPayments();
        if (response.data.error) {
          this.toast(response.data.message, 'is-danger');
        } else if (response.data.redirectUrl) {
          document.location.assign(response.data.redirectUrl);
        } else {
          this.toast(response.data.message);
        }
      })
        .finally(() => {
          this.preloading = false;
        });
    },
    onNextPage() {
      if (this.page < this.total) {
        this.page += 1;
        this.loadFailedPayments();
      }
    },
    onPrevPage() {
      if (this.page > 1) {
        this.page -= 1;
        this.loadFailedPayments();
      }
    },
    dateToString(date) {
      return date.toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },
    async loadFailedPayments() {
      const queryParams = {
        order: `${this.sortOrder === 'desc' ? '-' : '+'}${this.sortField}`,
        page: this.page,
        limit: this.perPage,
        userId: this.userId,
      };
      paymentsFailed(queryParams)
        .then((response) => {
          this.failedPayment = response.data.data;
          this.total = response.data.total;
        })
        .catch((error) => {
          this.error = error;
        });
    },
    toast(message, type = 'is-success') {
      this.$buefy.toast.open({ message, type, duration: 5000 });
    },
  },
};
</script>

<style scoped lang="stylus">
@media screen and (max-width: 768px)
  .pagination-previous.pagination-link, .pagination-next.pagination-link
    flex-grow unset
    width 48px
.column.is-7.retry-failed-content
  display flex
  flex-wrap wrap
  width 100%
.column.mw-320
  @media screen and (min-width: 320px)
    min-width 250px
.pagination
  margin-top: 20px
.button.is-success
  min-width 178px
.section-title >>> .columns
  display flex
  flex-wrap wrap
.button.button.is-success:hover:not(.is-disabled)
  background-color #17b548
  border-color #17b548
.button.button.is-success
  text-transform uppercase
  background-color #68BA62
.box.main-box
  outline: 1px solid #80808059
  .field
    margin-bottom: unset

.grid-module
  height auto

.grid-module__grid
  overflow visible

</style>
